/**
 * @file        src/components/Select.jsx
 * @description Themed dropdown that replaces the native <select> across admin
 *              forms. Matches the glass/light admin theme (var(--field) button,
 *              rounded menu, teal-highlighted active option) so option lists are
 *              styleable instead of the OS-rendered list. Controlled: parent owns
 *              `value`; `options` is [{ value, label }].
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-08-10 Claude — Added `disabled` (locks the trigger, matches native
 *                          <select disabled>) so the remaining native selects
 *                          across the admin console (busy-locked amendment
 *                          type pickers, etc.) can move to this component.
 *   - 2026-08-31 Claude — Portal the menu to document.body, positioned from the
 *                          trigger's bounding rect (same fix as RowMenu.jsx).
 *                          A `.panel.glass` ancestor uses backdrop-filter, which
 *                          creates a new stacking context — the menu's
 *                          `position:absolute` + `z-index:60` was scoped inside
 *                          THAT card only, so a later `.panel.glass` sibling
 *                          (e.g. the "Select a tenant…" placeholder card on the
 *                          Payment Gateways / Brands / Supplier Credentials
 *                          pages) painted over it. Portaling removes the menu
 *                          from that ancestor chain entirely.
 *   - 2026-10-07 Claude — Scrolling the option list closed it. The menu scrolls
 *                          itself (max-height), and the capture-phase window
 *                          'scroll' listener closed on ANY scroll — the menu's
 *                          own included — so options below the fold (Fare Type's
 *                          Flexi / Flexi Plus) were unreachable. A scroll inside
 *                          the menu is now ignored; a scroll of the page/modal
 *                          behind it re-anchors the menu instead of closing it
 *                          (closes once the trigger leaves the viewport). The
 *                          menu opens UPWARD when there is more room above, and
 *                          its height is clamped to the space it has.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MAX_H = 260;     // matches .nsel-menu's max-height in admin.css
const MENU_MIN_H = 120;     // never shrink below ~3 options, even on a tiny viewport
const MENU_GAP = 6;         // trigger ↔ menu
const VIEWPORT_EDGE = 8;    // keep clear of the window edge

export default function Select({ value, onChange, options = [], placeholder = 'Select', invalid = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { left, top, width }
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Place the menu against the trigger's CURRENT rect: below when it fits (or
  // when below is the roomier side), otherwise above, with its height clamped
  // to the space on that side so no option is painted outside the viewport.
  function place() {
    const r = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - MENU_GAP - VIEWPORT_EDGE;
    const above = r.top - MENU_GAP - VIEWPORT_EDGE;
    const up = below < MENU_MAX_H && above > below;
    const maxHeight = Math.max(MENU_MIN_H, Math.min(MENU_MAX_H, up ? above : below));
    return up
      ? { left: r.left, bottom: window.innerHeight - r.top + MENU_GAP, width: r.width, maxHeight }
      : { left: r.left, top: r.bottom + MENU_GAP, width: r.width, maxHeight };
  }

  function openMenu() {
    if (!open) setPos(place());
    setOpen((o) => !o);
  }

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // capture:true so a scroll on any ancestor (not just window) is seen. The
    // menu's OWN scroll is ignored — that is the user reaching the lower
    // options. Any other scroll re-anchors the fixed menu to the trigger, and
    // closes it only once the trigger has left the viewport.
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      const r = btnRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) { setOpen(false); return; }
      setPos(place());
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const current = options.find((o) => String(o.value) === String(value));

  return (
    <div className={`nsel${open ? ' open' : ''}`} ref={ref}>
      <button
        type="button"
        ref={btnRef}
        className={`nsel-btn${invalid ? ' invalid' : ''}`}
        onClick={openMenu}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={current ? '' : 'nsel-ph'}>{current ? current.label : placeholder}</span>
        <svg viewBox="0 0 24 24" className="nsel-chev" aria-hidden="true"><path fill="currentColor" d="m7 10 5 5 5-5z" /></svg>
      </button>
      {open && pos && createPortal(
        <div
          className="nsel-menu nsel-menu-portal"
          role="listbox"
          ref={menuRef}
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.width, maxHeight: pos.maxHeight }}
        >
          {options.map((o) => {
            const sel = String(o.value) === String(value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={sel}
                className={`nsel-opt${sel ? ' sel' : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                <span>{o.label}</span>
                {sel && <svg viewBox="0 0 24 24" className="nsel-tick" aria-hidden="true"><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
